import { PlacementYAnchor } from "building-types/annotation/annotationTypesEnum";
import type { Building } from "building-types/building/buildingTypes";
import type { Doc } from "building-types/doc";
import type { DocEntityId } from "building-types/doc/docEntityId";
import type { ParameterValue, SystemParameter } from "building-types/options/parameter";
import type { ContentTemplateInstance } from "building-types/views/planSets";
import { type SectionViewport } from "building-types/views/planSets";
import { SheetGeometryCategory } from "building-types/views/sheetGeometryCategory";
import type { SheetGeometry } from "building-types/views/sheetGeometryTypes";
import {
  sheetCircleGeometryNew,
  sheetPolylineGeometryNew,
  sheetTextGeometryNew,
} from "building-types/views/sheetGeometryTypes";
import { ZERO_POINTS } from "common/constants/scaleFactors";
import type { RWTable, Table } from "common/data/table";
import { constructSheetTitleNumber } from "common/utils/constructSheetTitleNumber";
import { percentNew, sheetPointsNew, worldInchesNew } from "common/utils/nominalNumbers";
import objectIsEmpty from "common/utils/objectIsEmpty";
import { resolveAnnotationParameterContext } from "doc/plans/annotationParameterContext";
import { Line2CollinearOverlap } from "geom/core";
import { line2CollinearOverlap, line2Normal } from "geom/line2";
import { polyLine2Arrow, polyLine2ByTwoPoints, polyLine2Move } from "geom/polyLine2";
import { type PolyLine2 } from "geom/polyLine2Types";
import { DEFAULT_REGULAR_FONT_SIZE } from "pdf/planFonts";
import { docEntityGetChildrenFromTable } from "../entity/docEntityGetChildrenFromTable";
import { DocEntityNodeRegistry } from "../entity/docEntityNodeRegistry";
import type { DocEntityNodeSource } from "../entity/docEntityNodeRegistryTypes";
import {
  assertIsContentTemplateDocEntityNode,
  docContentTemplateInstanceGetHostSheets,
} from "../entity/docEntityNodes/ContentTemplateDocEntityNode";
import { evaluateParameterDefaultValues } from "../parameterCoreTools";
import type { ParameterReplacementValueContext } from "../parameterTools";
import {
  evaluateFormulasParameterRefsInObject,
  getParameterReplacementValue,
  parameterValuesMerge,
} from "../parameterTools";
import { docCollectPlanSetTemplateParameterValues } from "../planSetTemplateTools";
import { SystemParameters } from "../systemParameters";
import { noteNew } from "../utils/annotation";
import type { PlanGenerationContext } from "./planGeometrySource";

interface CalloutReference {
  viewportId: string;
  viewportNumber: string;
  sheetNumber: string;
}

const defaultViewportNumber = "-";
const defaultSheetNumber = "---";

function getViewportNumber(viewport: SectionViewport, args: ParameterReplacementValueContext) {
  if (viewport.number === undefined) {
    return defaultViewportNumber;
  }

  if (typeof viewport.number === "string") {
    return viewport.number;
  }

  const parameterText = getParameterReplacementValue(viewport.number.id, args);
  return parameterText || defaultViewportNumber;
}

export function makeSheetGeometryForCalloutsFromSectionViews({
  building,
  planSetTemplateId,
  planSetTemplateConfigId,
  globalParameterValues,
  annotationParameterContext,
  generationDiagnostics,
}: PlanGenerationContext): SheetGeometry[] {
  const result: SheetGeometry[] = [];
  const { sectionViews } = building.doc;

  if (objectIsEmpty(sectionViews)) {
    return result;
  }

  const { paramValues } =
    annotationParameterContext ?? resolveAnnotationParameterContext(building, globalParameterValues ?? {});
  const params = { ...SystemParameters, ...building.doc.globalParameters, ...building.doc.localParameters };
  const refsForSectionView = collectSectionViewViewportRefs(
    building,
    planSetTemplateId,
    planSetTemplateConfigId,
    paramValues,
    params,
  );
  const collectedLeaderLines: [PolyLine2, string][] = [];

  for (const sectionViewId in sectionViews) {
    const sectionView = sectionViews[sectionViewId];
    const { startX, startY, endX, endY, name } = sectionView;

    const refs = refsForSectionView[sectionViewId];
    let refOffset = 0;

    if (!refs || refs.length === 0) {
      continue;
    }

    const leaderPl = polyLine2ByTwoPoints(startX, startY, endX, endY);

    const overlappingLeaders = collectedLeaderLines.filter(
      ([otherLeaderPl]) =>
        line2CollinearOverlap(
          otherLeaderPl[0],
          otherLeaderPl[1],
          otherLeaderPl[2],
          otherLeaderPl[3],
          leaderPl[0],
          leaderPl[1],
          leaderPl[2],
          leaderPl[3],
        ) !== Line2CollinearOverlap.NONE,
    );

    if (overlappingLeaders.length > 0) {
      const overlappingNames = overlappingLeaders.map(([, otherName]) => `"${otherName}"`).join(", ");
      const pluralize = overlappingLeaders.length > 1 ? "s" : "";

      const warningMessage = `Detected an overlap between callouts for section view "${name}" and section view${pluralize} ${overlappingNames}.`;
      generationDiagnostics?.push({
        code: "SECTION_VIEW_CALLOUT_OVERLAP",
        severity: "WARNING",
        category: SheetGeometryCategory.SECTION_VIEW_CALLOUT,
        message: warningMessage,
      });
    }
    collectedLeaderLines.push([leaderPl, name]);

    // Create an empty annotation to act as the building entity for source filtering.
    // Section views are not valid building entities
    const buildingEntity = noteNew({ text: "", x: 0, y: 0, labels: sectionView.calloutLabels });

    // Create callout tag geometry similar to foundation condition tags
    const size = worldInchesNew(24);
    const halfSize = size / 2;
    const radius = halfSize;

    // Calculate normal direction for the arrow
    const [nx, ny] = line2Normal(endX, endY, startX, startY);
    const dx = endX - startX;
    const dy = endY - startY;
    const len = Math.sqrt(dx * dx + dy * dy);

    if (len < Number.EPSILON) {
      continue;
    }
    const dirX = dx / len;
    const dirY = dy / len;

    // Create leader line
    const leader = sheetPolylineGeometryNew({
      paths: [leaderPl],
      fill: false,
      buildingEntity,
    });

    // Create foot (perpendicular line at the end)
    const footPl = polyLine2ByTwoPoints(endX, endY, endX + halfSize * nx, endY + halfSize * ny);
    const foot = sheetPolylineGeometryNew({
      paths: [footPl],
      linewidth: sheetPointsNew(2.5),
      fill: false,
      buildingEntity,
    });

    const arrows: SheetGeometry[] = [];
    const circles: SheetGeometry[] = [];

    // Create a callout for each reference
    for (const { sheetNumber, viewportId, viewportNumber } of refs) {
      const handle = { type: "VIEWPORT" as const, id: viewportId };
      const fontSize = DEFAULT_REGULAR_FONT_SIZE;
      const fontOffset = 0.1;

      // Create arrow at the start (perpendicular to the leader line)
      // Stack callouts along the normal direction
      const arrowSize = size * 1.5;
      const arrowHalfSize = arrowSize / 2;
      const stackOffset = refOffset * size;
      const arrowBaseX = startX + nx * arrowHalfSize - dirX * stackOffset;
      const arrowBaseY = startY + ny * arrowHalfSize - dirY * stackOffset;
      const arrowBasePl = polyLine2ByTwoPoints(arrowBaseX, arrowBaseY, arrowBaseX - nx, arrowBaseY - ny);
      const arrowPl = polyLine2Arrow(arrowBasePl, arrowSize, arrowHalfSize);
      const arrow = sheetPolylineGeometryNew({
        paths: [arrowPl],
        fill: true,
        fillColor: "black",
        fillOpacity: percentNew(1),
        strokeOpacity: percentNew(0),
        linewidth: ZERO_POINTS,
        handle,
        buildingEntity,
      });

      // Create circle background
      const circle = sheetCircleGeometryNew({
        x: arrowBaseX - nx * arrowHalfSize,
        y: arrowBaseY - ny * arrowHalfSize,
        radius,
        fill: true,
        fillOpacity: percentNew(1),
        fillColor: "white",
        handle,
        buildingEntity,
      });

      // Create horizontal line through circle
      const linePl = polyLine2ByTwoPoints(-radius, 0, radius, 0);
      polyLine2Move(linePl, circle.x, circle.y);
      const line = sheetPolylineGeometryNew({
        paths: [linePl],
        handle,
        buildingEntity,
      });

      // Create top text (viewport)
      const topText = sheetTextGeometryNew({
        text: viewportNumber,
        x: circle.x,
        y: circle.y,
        placementYAnchor: PlacementYAnchor.BOTTOM,
        handle,
        fontSize,
        boxVerticalAlignmentPercentageOffset: fontOffset,
        buildingEntity,
      });

      // Create bottom text (sheet)
      const bottomText = sheetTextGeometryNew({
        text: sheetNumber,
        x: circle.x,
        y: circle.y,
        placementYAnchor: PlacementYAnchor.TOP,
        handle,
        fontSize,
        boxVerticalAlignmentPercentageOffset: -fontOffset,
        buildingEntity,
      });

      arrows.push(arrow);
      circles.push(circle, line, topText, bottomText);
      refOffset++;
    }

    result.push(leader, foot, ...arrows, ...circles);
  }

  return result;
}

function resolveViewportSectionViewId(viewport: SectionViewport, values: Table<ParameterValue>): string | undefined {
  const sectionViewId =
    typeof viewport.contentSourceId === "string"
      ? viewport.contentSourceId
      : evaluateFormulasParameterRefsInObject(viewport.contentSourceId, values);

  if (!sectionViewId || typeof sectionViewId !== "string") {
    return undefined;
  }

  return sectionViewId;
}

function collectSectionViewViewportRefs(
  { doc }: Building,
  planSetTemplateId: DocEntityId<"PLAN_SET_TEMPLATE"> | undefined,
  planSetTemplateConfigId: DocEntityId<"PLAN_SET_TEMPLATE_CONFIG"> | undefined,
  paramValues: Table<ParameterValue>,
  params: Table<SystemParameter>,
) {
  // A viewport's section view can be driven by a plan set parameter. Resolve it from the rendered
  // config's values, falling back to the template's local-parameter defaults when no config is set.
  const values: RWTable<ParameterValue> = { ...paramValues };
  if (planSetTemplateId) {
    const planSetTemplateParamValues = planSetTemplateConfigId
      ? docCollectPlanSetTemplateParameterValues(
          doc,
          { type: "PLAN_SET_TEMPLATE", id: planSetTemplateId },
          { type: "CUSTOM", templateId: planSetTemplateId, templateConfigId: planSetTemplateConfigId },
        )
      : evaluateParameterDefaultValues(docEntityGetChildrenFromTable(doc.localParameters, planSetTemplateId));
    for (const parameterId in planSetTemplateParamValues) {
      values[parameterId] ??= planSetTemplateParamValues[parameterId];
    }
  }

  const parameterReplacementValueContext = { doc, values, params };
  const refsForSectionView: Record<string, CalloutReference[]> = {};
  const docEntityNodeSource = DocEntityNodeRegistry.getSource();

  for (const viewportId in doc.viewports) {
    const viewport = doc.viewports[viewportId];

    if (viewport.viewType !== "SECTION_VIEW") {
      continue;
    }

    let calloutCount = 0;

    const parentContentTemplateInstances = collectViewportContentTemplateInstances(doc, viewport, docEntityNodeSource);
    for (const contentTemplateInstance of parentContentTemplateInstances) {
      if (!contentTemplateInstance) {
        continue;
      }
      const instanceParameterValues = evaluateFormulasParameterRefsInObject(
        contentTemplateInstance.parameterValues,
        parameterReplacementValueContext.values,
      );
      const parameterValues: Table<ParameterValue> = { ...parameterReplacementValueContext.values };
      parameterValuesMerge(
        parameterValues,
        evaluateParameterDefaultValues(
          docEntityGetChildrenFromTable(doc.localParameters, contentTemplateInstance.contentTemplateId),
        ),
      );
      parameterValuesMerge(parameterValues, instanceParameterValues);
      const localParameterReplacementValueContext: typeof parameterReplacementValueContext = {
        ...parameterReplacementValueContext,
        values: parameterValues,
      };

      // Resolve in this instance's scope so its parameter overrides apply; an unset section view
      // parameter yields undefined and produces no callout.
      const sectionViewId = resolveViewportSectionViewId(viewport, parameterValues);
      if (!sectionViewId) {
        continue;
      }

      const calloutReferences = collectViewportSheetReferences(
        doc,
        contentTemplateInstance,
        viewport,
        localParameterReplacementValueContext,
        docEntityNodeSource,
        planSetTemplateId,
      );
      calloutCount += calloutReferences.length;
      (refsForSectionView[sectionViewId] ||= []).push(...calloutReferences);
    }

    // The viewport is in a sheet template that is not yet included in a plan set
    if (!planSetTemplateId && calloutCount === 0) {
      const sectionViewId = resolveViewportSectionViewId(viewport, parameterReplacementValueContext.values);
      if (sectionViewId) {
        (refsForSectionView[sectionViewId] ||= []).push({
          viewportId,
          viewportNumber: getViewportNumber(viewport, parameterReplacementValueContext),
          sheetNumber: defaultSheetNumber,
        });
      }
    }
  }

  return refsForSectionView;
}

function collectViewportSheetReferences(
  doc: Doc,
  contentTemplateInstance: ContentTemplateInstance,
  viewport: SectionViewport,
  parameterReplacementValueContext: ParameterReplacementValueContext,
  docEntityNodeSource: DocEntityNodeSource,
  planSetTemplateId: DocEntityId<"PLAN_SET_TEMPLATE"> | undefined,
) {
  // This content template instance could be nested in any number of drawing templates.
  const sheets = docContentTemplateInstanceGetHostSheets(doc, contentTemplateInstance.id, docEntityNodeSource);
  const references: CalloutReference[] = [];

  for (const sheet of sheets) {
    if (planSetTemplateId && sheet.parentId !== planSetTemplateId) {
      continue;
    }

    // constructSheetTitleNumber returns an empty string for undefined values. Using || here
    // ensures that we add a placeholder value of "---" instead of an empty string.
    const sheetNumber = constructSheetTitleNumber(sheet?.sheetNumberPrefix, sheet?.sheetNumber) || defaultSheetNumber;

    references.push({
      viewportId: viewport.id,
      viewportNumber: getViewportNumber(viewport, parameterReplacementValueContext),
      sheetNumber: sheetNumber,
    });
  }

  return references;
}

function collectViewportContentTemplateInstances(
  doc: Doc,
  viewport: SectionViewport,
  docEntityNodeSource: DocEntityNodeSource,
): ContentTemplateInstance[] {
  if (!viewport.parentId) {
    return [];
  }

  if (doc.contentTemplateInstances[viewport.parentId]) {
    return [doc.contentTemplateInstances[viewport.parentId]];
  }

  if (doc.contentTemplates[viewport.parentId]) {
    const contentTemplateNode = docEntityNodeSource.getNode(doc, { type: "CONTENT_TEMPLATE", id: viewport.parentId });
    assertIsContentTemplateDocEntityNode(contentTemplateNode);

    return contentTemplateNode.getContentTemplateInstances().map((node) => node.getDocEntity());
  }

  return [];
}
